<script module>
  export const i18n = {
    de: {
      Close: 'Schließen'
    },
    nb: {
      Close: 'Lukk'
    },
    sv: {
      Close: 'Stäng'
    },
    da: {
      Close: 'Luk'
    }
  }
</script>

<script>
  import { cubicIn, cubicOut } from 'svelte/easing'
  import { fly, fade } from 'svelte/transition'
  import { tick } from 'svelte'

  import { register } from '$lib/utils/text.svelte.js'
  import { SELECTOR, capture } from '$lib/capture.js'
  import Symbol from '$lib/Symbol.svelte'
  import Button from '$lib/Button.svelte'

  let text = register(i18n)

  /** @type {{
    href?: string,
    position?: 'top'|'center',
    onclose?: () => void,
    close?: Svelte.Snippet,
    children: Svelte.Snippet,
    footer?: Svelte.Snippet
  }} */
  const {
    href,
    position = 'center',
    onclose,
    children,
    close,
    footer
  } = $props()

  let modal, content
  let transitioning = false
  const media =
    typeof window === 'undefined' ? null : window.matchMedia('(width < 800px)')

  $effect(() => {
    if (!transitioning && modal) {
      modal.querySelector(SELECTOR)?.focus()
    }
  })

  $effect.pre(() => {
    if (!content) return
    if (transitioning) return
    if (media?.matches) return

    const before = content.getBoundingClientRect()

    /** @type {Animation} */
    let animation
    tick().then(async () => {
      const after = content.getBoundingClientRect()

      if (before.height === after.height) return

      animation = content.animate(
        [{ height: `${before.height}px` }, { height: `${after.height}px` }],
        {
          duration: 600,
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
        }
      )
    })

    return () => {
      animation?.commitStyles()
      animation?.cancel()
    }
  })

  let target
  const onmousedown = (event) => {
    target = event.target
  }
  const onmouseup = (event) => {
    if (event.currentTarget === target) {
      onclose?.()
    }
  }

  const handleEsc = (event) => {
    if (event.key === 'Escape') {
      onclose?.()
    }
  }
</script>

<svelte:document on:keydown={handleEsc} />

<div
  bind:this={modal}
  use:capture
  class="modal {position}"
  onintrostart={() => (transitioning = true)}
  onintroend={() => (transitioning = false)}
  in:fade={{ duration: 200, easing: cubicOut }}
  out:fade={{ duration: 200, easing: cubicIn }}>
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="scrollable" {onmousedown} {onmouseup}>
    <div
      bind:this={content}
      class="window theme-reset"
      in:fly={{ opacity: 1, y: '30%', duration: 250, easing: cubicOut }}
      out:fly={{ opacity: 1, y: '30%', duration: 250, easing: cubicIn }}>
      <div class="close">
        {#if close}
          {@render close()}
        {:else}
          <Button
            type={href ? null : 'button'}
            muted
            size="xs"
            {href}
            onclick={(event) => {
              if (onclose) {
                onclose()
                event.preventDefault()
              }
            }}>
            <Symbol name="cross" prefix size="sm" />
            {text`Close`}
          </Button>
        {/if}
      </div>
      <div class="body">
        {@render children()}
      </div>
      {#if footer}
        <div class="footer">
          {@render footer()}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  :root:has(.modal) {
    overflow: hidden;
  }

  .modal {
    display: flex;
    width: 100%;
    height: 100dvh;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 2147483647;
    background-color: rgba(31, 31, 34, 0.5);
  }

  .scrollable {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    position: relative;
    z-index: 1;
    overflow: auto;
    overscroll-behavior: contain;
  }

  .modal.top .scrollable {
    align-items: flex-start;
  }

  @media (min-width: 800px) {
    .scrollable {
      padding: 2rem var(--document-margin);
    }
  }

  .window {
    --modal-padding-x: 1.5rem;
    --modal-padding-y: 2rem;

    display: grid;
    grid-template-rows: 1fr auto;
    width: 100%;
    min-height: 100%;
    padding: 5rem var(--modal-padding-x) var(--modal-padding-y);
    margin: auto 0;
    position: relative;
    overflow: hidden;
    background-color: var(--current-background, rgb(var(--rgb-white)));
  }

  .modal.top .window {
    margin-top: 0;
  }

  @media (width >= 800px) {
    .window {
      --modal-padding-x: 2.5rem;
      --modal-padding-y: 2.5rem;

      min-height: auto;
      max-width: 48rem;
      padding-top: var(--modal-padding-y);
      border-radius: var(--border-radius-lg);
    }
  }

  .body {
    display: grid;
  }

  .footer {
    padding: 1.5rem var(--modal-padding-x);
    margin: 1.5rem calc(var(--modal-padding-x) * -1)
      calc(var(--modal-padding-y) * -1);
    background: var(--background-alt);
  }

  .footer:empty {
    display: none;
  }

  .close {
    display: flex;
    align-items: center;
    float: right;
    position: absolute;
    top: var(--modal-padding-x);
    right: var(--modal-padding-x);
    z-index: 1;
    user-select: none;
  }
</style>
